import { Button, Card, Col, Form, Input, Row, Select, Table } from 'antd';
import React, { useEffect, useRef, useState } from 'react';
import { DownloadOutlined, UndoOutlined,StarOutlined,SearchOutlined,ArrowUpOutlined,ArrowDownOutlined } from '@ant-design/icons';
import { SaleBudgetService } from 'libs/shared-services/masters/src/lib/sale-budget-service';
import Highlighter from 'react-highlight-words';
import { Excel } from 'antd-table-saveas-excel';


const BudgetVarianceReport = () => {
  const service = new SaleBudgetService();
  const [data, setData] = useState([]);
  const { Option } = Select;
  const [form] = Form.useForm();
  const [searchText, setSearchText] = useState("");
  const [searchedColumn, setSearchedColumn] = useState("");
  const searchInput = useRef(null);
  const [page, setPage] = React.useState(1);


  const getBudgetVarianceReport = (month = undefined, year = undefined) => {
    service.getBudgetVarianceReport(month, year).then((res) => {
      if (res.status) {
        setData(res.data);
      }
    });
  };

  const monthMapping = {
    1: 'January',
    2: 'February',
    3: 'March',
    4: 'April',
    5: 'May',
    6: 'June',
    7: 'July',
    8: 'August',
    9: 'September',
    10: 'October',
    11: 'November',
    12: 'December',
  };

  const handleSearch = (selectedKeys, confirm, dataIndex) => {
    confirm();
    setSearchText(selectedKeys[0]);
    setSearchedColumn(dataIndex);
  };
  const handleReset = (clearFilters) => {
    clearFilters();
    setSearchText("");
  };

  const getColumnSearchProps = (dataIndex: string) => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
    }) => (
      <div style={{ padding: 8 }}>
        <Input
          ref={searchInput}
          placeholder={`Search ${dataIndex}`}
          value={selectedKeys[0]}
          onChange={(e) =>
            setSelectedKeys(e.target.value ? [e.target.value] : [])
          }
          onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
          style={{ width: 188, marginBottom: 8, display: "block" }}
        />
        <Button
          type="primary"
          onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
          icon={<SearchOutlined />}
          size="small"
          style={{ width: 90, marginRight: 8 }}
        >
          Search
        </Button>
        <Button
          size="small"
          style={{ width: 90 }}
          onClick={() => {
            handleReset(clearFilters);
            setSearchedColumn(dataIndex);
            confirm({ closeDropdown: true });
          }}
        >
          Reset
        </Button>
      </div>
    ),
    filterIcon: (filtered) => (
      <SearchOutlined
        type="search"
        style={{ color: filtered ? "#1890ff" : undefined }}
      />
    ),
    onFilter: (value, record) =>
      record[dataIndex]
        ? record[dataIndex]
          .toString()
          .toLowerCase()
          .includes(value.toLowerCase())
        : false,
    onFilterDropdownVisibleChange: (visible) => {
      if (visible) {
        setTimeout(() => searchInput.current.select());
      }
    },
    render: (text) =>
      text ? (
        searchedColumn === dataIndex ? (
          <Highlighter
            highlightStyle={{ backgroundColor: "#ffc069", padding: 0 }}
            searchWords={[searchText]}
            autoEscape
            textToHighlight={text.toString()}
          />
        ) : (
          text
        )
      ) : null,
  });

  const Columns: any[] = [
    {
      title: 'S No',
      render: (text, object, index) => (index == data.length ? null : index + 1),
    },
    {
      title: 'Sale Person',
      dataIndex: 'employee_name',
      render: (text, r) => text ?? r.sbSalePerson,
      sorter: (a, b) => a.employee_name.localeCompare(b.employee_name),
      sortDirections: ['descend', 'ascend'],
      ...getColumnSearchProps('employee_name')

    },
    {
      title: 'Month',
      dataIndex: 'confirmedBudgetMonth',
      render: (text, r) => monthMapping[Number(text)] ?? monthMapping[Number(r.month)],
      sorter: (a, b) => {
        const monthValueA = monthMapping[a.confirmedBudgetMonth] || a.confirmedBudgetMonth;
        const monthValueB = monthMapping[b.confirmedBudgetMonth] || b.confirmedBudgetMonth;
  
        return monthValueA - monthValueB;
      },
      sortDirections: ['ascend', 'descend'],
    },
    {
      title: 'Year',
      dataIndex: 'confirmedBudgetYear',
      render: (text, r) => text ?? r.year,
      sorter: (a, b) => a.confirmedBudgetYear - b.confirmedBudgetYear,
      sortDirections: ['descend', 'ascend'],

    },
    {
      title: 'Country',
      dataIndex: 'country_name',
      render:(e,val)=>{
        return (val.country_name? val.country_name:'-')
      },
      sorter: (a, b) => a.country_name.localeCompare(b.country_name),
      sortDirections: ['descend', 'ascend'],
      ...getColumnSearchProps('country_name')

    },
    {
      title: 'Budget',
      dataIndex: 'total_budget_containers',
      render: (text) => (text != null ? text : 0),
      sorter: (a, b) => a.total_budget_containers - b.total_budget_containers,
      sortDirections: ['descend', 'ascend'],

    },
    {
      title: 'Actual Containers',
      dataIndex: 'total_confirmed_containers',
      sorter: (a, b) => a.total_confirmed_containers - b.total_budget_containers,
      sortDirections: ['descend', 'ascend'],
      render: (text, record) => {
        const budget = record.total_budget_containers != null ? record.total_budget_containers : 0;
        return (
          <>
            {text !== null ? text : 0}
            {budget === 0 && text > 0 && ( // Check if budget is 0 but actual containers are present
              <StarOutlined style={{ color: 'Orange' }} />
            )}
          </>
        );
      },
    },
    {
      title: 'Variance',
      render: (text, record) => {
        const budget = record.total_budget_containers != null ? record.total_budget_containers : 0;
        const total_confirmed_containers = record.total_confirmed_containers != null ? record.total_confirmed_containers : 0;
        const variance = Math.abs(budget - total_confirmed_containers);
        let icon;
        if (total_confirmed_containers >= budget) {
          icon = <ArrowUpOutlined style={{ color: 'green', marginRight: 4 }} />;
        } else {
          icon = <ArrowDownOutlined style={{ color: 'red', marginRight: 4 }} />;
        }
        return (
          <span>
            {variance}
            {icon}
          </span>
        );
      },
      sorter: (a, b) => {
        const budgetA = a.totalBudgetContainers != null ? a.totalBudgetContainers : 0;
        const total_confirmed_containersA = a.total_confirmed_containers != null ? a.total_confirmed_containers : 0;
        const varianceA = budgetA - total_confirmed_containersA;
  
        const budgetB = b.totalBudgetContainers != null ? b.totalBudgetContainers : 0;
        const total_confirmed_containersB = b.total_confirmed_containers != null ? b.total_confirmed_containers : 0;
        const varianceB = budgetB - total_confirmed_containersB;
  
        return varianceA - varianceB;
      },
      sortDirections: ['descend', 'ascend'],
    },
  ];

  const resetHandler = () => {
    form.resetFields();
    setData([]);
    getBudgetVarianceReport()
  };

  const onFinish = (values) => {
    console.log(values,"valuesssssssssssssssssssss")
    const month = values.month ? parseInt(values.month, 10) : undefined;
    console.log(month,"monthhhhhhhhhhh frontendddddddddddddd")
    const year = values.year ? parseInt(values.year, 10) : undefined;
    console.log(year,"yearrrrrrrrrrrrrr frontendddddddddddddd")

    getBudgetVarianceReport(month, year);
  };

  useEffect(() => {
    getBudgetVarianceReport(); 
  }, []); 

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 20 }, (_, i) => currentYear - i);


  const exportExcel = () => {
    let totalBudget = 0;
    let totalActualContainers = 0;
    let totalVariance = 0;
  
    // Preprocess the data
    const processedData = data.map((item, index) => {
      const budget = Number(item.total_budget_containers) || 0;
      const actualContainers = Number(item.total_confirmed_containers) || 0;
      const variance = budget - actualContainers;
      const varianceIcon = actualContainers >= budget ? '↑' : '↓';
  
      // Accumulate totals correctly as numbers
      totalBudget += budget;
      totalActualContainers += actualContainers;
      totalVariance += variance;
  
      return {
        sNo: index + 1, // Number
        salePerson: item.employee_name ?? item.sbSalePerson,
        month: monthMapping[Number(item.confirmedBudgetMonth)] ?? monthMapping[Number(item.month)],
        year: item.confirmedBudgetYear ?? item.year,
        country: item.country_name ?? '-',
        budget, // Keep as number
        actualContainers, // Keep as number
        variance: `${variance} ${varianceIcon}`, // String since it includes an icon
      };
    });
  
    // Append total row (use null for sNo)
    processedData.push({
      sNo: null, // Keep null to avoid type errors
      salePerson: 'Total',
      month: '',
      year: '',
      country: '',
      budget: totalBudget, // Ensure it's a number
      actualContainers: totalActualContainers, // Ensure it's a number
      variance: totalVariance.toString(), // Convert to string explicitly
    });
  
    const excel = new Excel();
    excel
      .addSheet('budget-variance-report')
      .addColumns([
        { title: 'S No', dataIndex: 'sNo', width: 50 },
        { title: 'Sale Person', dataIndex: 'salePerson', width: 150 },
        { title: 'Month', dataIndex: 'month', width: 100 },
        { title: 'Year', dataIndex: 'year', width: 80 },
        { title: 'Country', dataIndex: 'country', width: 100 },
        { title: 'Budget', dataIndex: 'budget', width: 100 },
        { title: 'Actual Containers', dataIndex: 'actualContainers', width: 150 },
        { title: 'Variance', dataIndex: 'variance', width: 150 },
      ])
      .addDataSource(processedData, { str2num: true })
      .saveAs('budget-variance.xlsx');
  };
  
  
  
  
  

  return (
    <div>
      <Card
        size="small"
        title={<span style={{ color: 'white' }}>Budget Variance Report</span>}
        style={{ textAlign: 'center' }}
        headStyle={{ backgroundColor: '#69c0ff', border: 0 }}
        extra={
          <div>
            <Button icon={<DownloadOutlined />} onClick={() => { exportExcel(); }} style={{ marginRight: 30 }}>
              Get Excel
            </Button>
          </div>
        }
      >
        <Form form={form} onFinish={onFinish} layout="vertical">
          <Row gutter={16}>
            <Col xs={24} sm={24} md={8} lg={8} xl={6}>
            <Form.Item name="month" label="Month">
  <Select placeholder="Select Month" allowClear showSearch filterOption={(input, option) =>
    option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
  }>
    {Object.entries(monthMapping).map(([key, value]) => (
      <Option key={key} value={parseInt(key)}>
        {value}
      </Option>
    ))}
  </Select>
</Form.Item>
            </Col>
            <Col xs={24} sm={24} md={8} lg={8} xl={6}>
            <Form.Item name="year" label="Year">
                <Select placeholder="Select Year" allowClear showSearch>
                  {years.map((year) => (
                    <Option key={year} value={year}>
                      {year}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col style={{ paddingLeft: '10px', marginTop: '30px' }}>
              <Button  type="primary" htmlType="submit">
                Get Report
              </Button>
              </Col>
              <Col style={{ paddingLeft: '10px', marginTop: '30px' }}>
              <Button
                onClick={resetHandler}
                type="primary"
              
              >
                Reset
              </Button>
            </Col>
          </Row>
        </Form>
        <Table columns={Columns} dataSource={data} pagination={{
          pageSize:100,
          onChange(current) {
            setPage(current);
          }
        }}
        summary={(pageData) => {
          let totalBudgetContainers = 0;
          let totalContainers = 0;
          let totalNetAmount = 0;
        
          pageData.forEach(({ total_budget_containers,total_confirmed_containers, invoiceInr }) => {
            const totalvariance=parseFloat(total_confirmed_containers)-parseFloat(total_budget_containers)
              totalBudgetContainers += parseFloat(total_budget_containers) || 0;
              totalContainers += parseFloat(total_confirmed_containers) || 0;
              totalNetAmount = totalContainers-totalBudgetContainers
            });
        
          return (
            <Table.Summary.Row>
              <Table.Summary.Cell index={4} colSpan={4}>
                Total
              </Table.Summary.Cell>
              <Table.Summary.Cell index={5}>
                {totalBudgetContainers.toFixed(2)}
              </Table.Summary.Cell>

              <Table.Summary.Cell index={6}>
                {totalContainers.toFixed(2)}
              </Table.Summary.Cell>
              <Table.Summary.Cell index={7}>
                {totalNetAmount.toFixed(2)}
              </Table.Summary.Cell>
            </Table.Summary.Row>
          );
        }}
        />
      </Card>
    </div>
  );
};

export default BudgetVarianceReport;
